import { useQueryClient, skipToken } from "@tanstack/react-query";
import {
  useListModelsApiV1TrainingModelsGet,
  useListModelsApiV1ModelsCatalogGet,
  useTriggerTrainingApiV1TrainingPost,
  useCreateValidationApiV1ModelValidationPost,
  useGetDatasetSourcesApiV1DatasetDatasetIdLabelSourcesGet,
} from "../../../../vl-api/vlComponents";
import { App } from "antd";
import { invalidateActiveQueryByOperationId } from "../../../../vl-api/vlUtils";
import { useEffect, useState, useMemo } from "react";
import { ModelJobTypes } from "./TrainModelButton";
import { Option } from "views/uikit/FormFields/SelectList";

export const useTriggerTraining = (
  closeModal: () => void,
  isOpen: boolean,
  datasetId: string,
) => {
  const { notification } = App.useApp();
  const queryClient = useQueryClient();
  const [modelJobType, setModelJobType] = useState<ModelJobTypes | null>(null);
  const [showAllModels, setShowAllModels] = useState<boolean>(true);

  const shouldFetchForValidate =
    modelJobType === "VALIDATE" && !!datasetId && isOpen;

  // Fetch label sources from API (only when in validate step)
  const { data: labelSourcesData } =
    useGetDatasetSourcesApiV1DatasetDatasetIdLabelSourcesGet<{
      sources: Array<{ source_id: string; source: string }>;
    }>(
      { pathParams: { datasetId } },
      {
        enabled: !!datasetId && isOpen,
      },
    );

  // Build source options from API data
  const sourceOptions = useMemo<Option[]>(() => {
    const sources = labelSourcesData?.sources || [];

    if (sources.length === 0) {
      return [{ value: "NONE", label: "None" }];
    }

    return sources.map((source: { source_id: string; source: string }) => ({
      value: source.source_id,
      label: source.source,
    }));
  }, [labelSourcesData]);

  const { data, isLoading: isLoadingModels } =
    useListModelsApiV1TrainingModelsGet({});

  const { data: catalogData } = useListModelsApiV1ModelsCatalogGet(
    shouldFetchForValidate && !showAllModels ? {} : skipToken,
    { enabled: shouldFetchForValidate && !showAllModels },
  );

  useEffect(() => {
    if (!isOpen) {
      setModelJobType(null);
      setShowAllModels(true);
    }
  }, [isOpen]);

  const { data: compatibleCatalogData } = useListModelsApiV1ModelsCatalogGet(
    shouldFetchForValidate && showAllModels
      ? { queryParams: { supported_dataset_id: datasetId } }
      : skipToken,
    { enabled: shouldFetchForValidate && showAllModels },
  );

  const compatibleModelIds = useMemo(
    () => new Set((compatibleCatalogData?.models ?? []).map((m) => m.id)),
    [compatibleCatalogData],
  );

  const modelData = useMemo(() => {
    const models = showAllModels
      ? (compatibleCatalogData?.models ?? [])
      : (catalogData?.models ?? []);

    return models?.map((model: any) => {
      const isCompatible = showAllModels
        ? true
        : compatibleModelIds.size > 0 && compatibleModelIds.has(model.id);
      return {
        value: model.id,
        label: model.model_name,
        name: model.model_name,
        version: model?.version ?? "",
        accuracy: model.accuracy,
        date: model.last_updated
          ? new Date(model.last_updated).toLocaleDateString()
          : "",
        isCompatible,
      };
    });
  }, [catalogData, showAllModels, compatibleModelIds, compatibleCatalogData]);

  const handleModelToggle = (value: boolean) => {
    setShowAllModels(value);
  };

  const createNotificationHandler = (isValidate: boolean) => ({
    onSuccess: () => {
      const message = isValidate ? "Validation started" : "Training started";
      const description = isValidate
        ? "Your dataset was successfully sent for validation."
        : "Your dataset was successfully sent to training.";

      notification.success({
        message,
        description,
      });
      closeModal();

      if (isValidate) {
        invalidateActiveQueryByOperationId(
          queryClient,
          "getValidationTasksApiV1ModelValidationTasksGet",
        );
      } else {
        invalidateActiveQueryByOperationId(
          queryClient,
          "getTrainingTasksApiV1TrainingTasksGet",
        );
      }
    },
    onError: (e: any) => {
      notification.error({
        message: e.detail || e.message,
        duration: 10,
      });
    },
  });

  const triggerTrainingMutation = useTriggerTrainingApiV1TrainingPost({
    ...createNotificationHandler(false),
  });

  const triggerValidationMutation = useCreateValidationApiV1ModelValidationPost(
    {
      ...createNotificationHandler(true),
    },
  );

  const modelOptions =
    modelJobType === "VALIDATE"
      ? modelData
      : (data as any)?.models?.map((value: string) => ({
          value,
          label: value,
        }));

  return {
    modelsOptions: modelOptions,
    isLoadingModels: isLoadingModels,
    modelJobType,
    sourceOptions,
    setModelJobType,
    triggerTrainingMutation,
    triggerValidationMutation,
    handleModelToggle,
  };
};
